import React, { Component, Suspense, lazy } from 'react';
import { Navigate, Routes, Route } from 'react-router-dom';
import Layout from './components/layout/Layout';
import { AuthProvider } from './components/auth/AuthProvider';
import { RequireAuth } from './components/auth/RequireAuth';
import RequireSuperAdmin from './components/auth/RequireSuperAdmin';
import ToastContainer from './components/common/ToastContainer';
import LowPointsWarning from './components/common/LowPointsWarning';
import StatusPage from './components/common/StatusPage';
import HomePage from './pages/client/HomePage';
import LoginPage from './pages/client/LoginPage';
import RegisterPage from './pages/client/RegisterPage';

const ProfilePage = lazy(() => import('./pages/client/ProfilePage'));
const RechargePage = lazy(() => import('./pages/client/RechargePage'));
const PaymentResultPage = lazy(() => import('./pages/client/PaymentResultPage'));
const VideoProjectsPage = lazy(() => import('./pages/client/VideoProjectsPage'));
const VideoAssetsPage = lazy(() => import('./pages/client/VideoAssetsPage'));
const VideoProjectNewPage = lazy(() => import('./pages/client/VideoProjectNewPage'));
const VideoScriptPage = lazy(() => import('./pages/client/video-pipeline/VideoScriptPage'));
const VideoCharactersPage = lazy(() => import('./pages/client/video-pipeline/VideoCharactersPage'));
const VideoStoryboardPage = lazy(() => import('./pages/client/video-pipeline/VideoStoryboardPage'));
const VideoImagesPage = lazy(() => import('./pages/client/video-pipeline/VideoImagesPage'));
const VideoPage = lazy(() => import('./pages/client/video-pipeline/VideoPage'));
const ImageGeneratePage = lazy(() => import('./pages/client/ImageGeneratePage'));
const TaskListPage = lazy(() => import('./pages/client/TaskListPage'));
const RoleManagementPage = lazy(() => import('./pages/client/RoleManagementPage'));
const DocsPage = lazy(() => import('./pages/client/DocsPage'));
const NotificationsPage = lazy(() => import('./pages/client/NotificationsPage'));
const NotificationDetailPage = lazy(() => import('./pages/client/NotificationDetailPage'));
const KnowledgePage = lazy(() => import('./pages/client/KnowledgePage'));
const KnowledgeDetailPage = lazy(() => import('./pages/client/KnowledgeDetailPage'));
const AdminPage = lazy(() => import('./pages/admin/AdminPage'));
const AdminMonitoringPage = lazy(() => import('./pages/admin/AdminMonitoringPage'));
const AdminPointsPage = lazy(() => import('./pages/admin/AdminPointsPage'));
const AdminPointUserPage = lazy(() => import('./pages/admin/AdminPointUserPage'));

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<{ children: React.ReactNode }, ErrorBoundaryState> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      return (
        <StatusPage
          code={500}
          fullScreen
          primaryAction={{
            label: '重试',
            onClick: this.handleReset,
          }}
        />
      );
    }
    return this.props.children;
  }
}

function PageLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-primary px-6 text-content-primary">
      <div className="flex flex-col items-center gap-4 text-center">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-border-default border-t-primary" />
        <p className="text-sm text-content-muted">正在加载页面...</p>
      </div>
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ToastContainer />
        <LowPointsWarning />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/workspace/:id" element={<Navigate to="/video-projects" replace />} />
            <Route element={<Layout />}>
              <Route path="/" element={<HomePage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/recharge" element={<RequireAuth><RechargePage /></RequireAuth>} />
              <Route path="/payment/result" element={<RequireAuth><PaymentResultPage /></RequireAuth>} />
              <Route path="/workspace" element={<Navigate to="/video-projects" replace />} />
              <Route
                path="/video-projects"
                element={
                  <RequireAuth>
                    <VideoProjectsPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/video-projects/new"
                element={
                  <RequireAuth>
                    <VideoProjectNewPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/video-assets"
                element={
                  <RequireAuth>
                    <VideoAssetsPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/video-assets/:projectId"
                element={
                  <RequireAuth>
                    <VideoAssetsPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/video-projects/:projectId/script"
                element={
                  <RequireAuth>
                    <VideoScriptPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/video-projects/:projectId/characters"
                element={
                  <RequireAuth>
                    <VideoCharactersPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/video-projects/:projectId/storyboard"
                element={
                  <RequireAuth>
                    <VideoStoryboardPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/video-projects/:projectId/images"
                element={
                  <RequireAuth>
                    <VideoImagesPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/video-projects/:projectId/video"
                element={
                  <RequireAuth>
                    <VideoPage />
                  </RequireAuth>
                }
              />
              <Route path="/ai-generate" element={<Navigate to="/video-projects" replace />} />
              <Route
                path="/image-generate"
                element={
                  <RequireAuth>
                    <ImageGeneratePage />
                  </RequireAuth>
                }
              />
              <Route
                path="/tasks"
                element={
                  <RequireAuth>
                    <TaskListPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/roles"
                element={
                  <RequireAuth>
                    <RoleManagementPage />
                  </RequireAuth>
                }
              />
              <Route path="/pricing" element={<Navigate to="/" replace />} />
              <Route path="/docs" element={<DocsPage />} />
              <Route path="/401" element={<StatusPage code={401} />} />
              <Route path="/notifications" element={<NotificationsPage />} />
              <Route path="/notifications/:id" element={<NotificationDetailPage />} />
              <Route path="/knowledge" element={<KnowledgePage />} />
              <Route path="/knowledge/:id" element={<KnowledgeDetailPage />} />
              <Route
                path="/admin"
                element={
                  <RequireAuth>
                    <AdminPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/monitoring"
                element={
                  <RequireAuth>
                    <AdminMonitoringPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/points"
                element={
                  <RequireAuth>
                    <RequireSuperAdmin>
                      <AdminPointsPage />
                    </RequireSuperAdmin>
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/points/:userId"
                element={
                  <RequireAuth>
                    <RequireSuperAdmin>
                      <AdminPointUserPage />
                    </RequireSuperAdmin>
                  </RequireAuth>
                }
              />
              <Route path="*" element={<StatusPage code={404} />} />
            </Route>
          </Routes>
        </Suspense>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
